// src/features/groups/GroupInstanceManager.ts
/**
 * Group Instance Manager - Manages master group instances with auto-sync
 * Enables reusable group components across diagrams
 */

import { GroupInstance, GroupInstanceSyncResult } from '@/types/database.types';
import { getGroup } from '@/services/groupService';
import { v4 as uuidv4 } from 'uuid';

export class GroupInstanceManager {
    private instances: Map<string, GroupInstance> = new Map();
    private listeners: Set<() => void> = new Set();

    /**
     * Create a new instance from a master group
     * @param masterGroupId UUID of the master group
     * @param position Initial position on canvas
     * @returns Promise<GroupInstance | null>
     */
    async createInstance(
        masterGroupId: string,
        position: { x: number; y: number }
    ): Promise<GroupInstance | null> {
        try {
            // Fetch master group from Supabase
            const { data: masterGroup, error } = await getGroup(masterGroupId);

            if (error || !masterGroup) {
                console.error('Failed to fetch master group:', error);
                return null;
            }

            // Generate unique instance ID
            const instanceId = `group-instance-${uuidv4()}`;

            // Transform master items to instance items with unique IDs
            // Each instance needs its own IDs to avoid conflicts
            const instanceItems = this.transformMasterItems(
                masterGroup.items,
                position
            );

            // Create instance
            const instance: GroupInstance = {
                id: instanceId,
                type: 'GROUP_INSTANCE',
                masterGroupId: masterGroup.id,
                masterGroupVersion: masterGroup.version,
                name: masterGroup.name,
                position: { ...position },
                rotation: 0,
                scale: 1,
                items: instanceItems,
                icons: [...masterGroup.icons],
                colors: [...masterGroup.colors],
                locked: false,
                metadata: {
                    createdAt: Date.now(),
                    lastSyncedAt: Date.now(),
                    isSyncEnabled: true
                }
            };

            this.instances.set(instanceId, instance);
            this.notifyListeners();

            return instance;
        } catch (error) {
            console.error('Error creating group instance:', error);
            return null;
        }
    }

    /**
     * Sync a specific instance with its master group
     * @param instanceId Instance ID to sync
     * @returns Promise<GroupInstanceSyncResult>
     */
    async syncInstance(instanceId: string): Promise<GroupInstanceSyncResult> {
        const instance = this.instances.get(instanceId);

        if (!instance) {
            return {
                success: false,
                updated: false,
                error: 'Instance not found'
            };
        }

        if (!instance.metadata.isSyncEnabled) {
            return {
                success: true,
                updated: false,
                error: 'Sync is disabled for this instance'
            };
        }

        try {
            // Fetch latest master group
            const { data: masterGroup, error } = await getGroup(instance.masterGroupId);

            if (error || !masterGroup) {
                return {
                    success: false,
                    updated: false,
                    error: 'Master group not found or deleted'
                };
            }

            // Check if update needed
            if (masterGroup.version === instance.masterGroupVersion) {
                return {
                    success: true,
                    updated: false
                };
            }

            // Create ID mapping to preserve instance IDs
            const idMapping = this.createIdMapping(instance.items, masterGroup.items);

            // Transform master items using existing instance IDs where possible
            const updatedItems = this.transformMasterItemsWithMapping(
                masterGroup.items,
                instance.position,
                idMapping
            );

            // Imported/model data may be frozen by Immer. Keep the manager's
            // updates immutable instead of assigning into a shared object.
            const updatedInstance: GroupInstance = {
                ...instance,
                items: updatedItems,
                icons: [...masterGroup.icons],
                colors: [...masterGroup.colors],
                name: masterGroup.name,
                masterGroupVersion: masterGroup.version,
                metadata: {
                    ...instance.metadata,
                    lastSyncedAt: Date.now()
                }
            };

            this.instances.set(instanceId, updatedInstance);
            this.notifyListeners();

            return {
                success: true,
                updated: true,
                newVersion: masterGroup.version
            };
        } catch (error) {
            console.error('Error syncing instance:', error);
            return {
                success: false,
                updated: false,
                error: error instanceof Error ? error.message : 'Unknown error'
            };
        }
    }

    /**
     * Sync all instances in the diagram
     * @returns Promise<Map<string, GroupInstanceSyncResult>>
     */
    async syncAllInstances(): Promise<Map<string, GroupInstanceSyncResult>> {
        const results = new Map<string, GroupInstanceSyncResult>();

        // Sync all instances in parallel
        const syncPromises = Array.from(this.instances.keys()).map(async (instanceId) => {
            const result = await this.syncInstance(instanceId);
            return { instanceId, result };
        });

        const syncResults = await Promise.all(syncPromises);

        syncResults.forEach(({ instanceId, result }) => {
            results.set(instanceId, result);
        });

        return results;
    }

    /**
     * Check if an instance needs sync (version mismatch)
     * @param instanceId Instance ID to check
     * @returns Promise<boolean>
     */
    async needsSync(instanceId: string): Promise<boolean> {
        const instance = this.instances.get(instanceId);
        if (!instance) return false;

        try {
            const { data: masterGroup, error } = await getGroup(instance.masterGroupId);
            if (error || !masterGroup) return false;

            return masterGroup.version > instance.masterGroupVersion;
        } catch {
            return false;
        }
    }

    /**
     * Detach instance from master (convert to standalone group)
     * Returns the instance data that can be converted to an ElementGroup
     * @param instanceId Instance ID to detach
     * @returns GroupInstance | null
     */
    detachInstance(instanceId: string): GroupInstance | null {
        const instance = this.instances.get(instanceId);
        if (!instance) return null;

        // Remove from instances (caller should convert to ElementGroup)
        this.instances.delete(instanceId);
        this.notifyListeners();

        return instance;
    }

    /**
     * Toggle sync for an instance
     * @param instanceId Instance ID
     * @param enabled Enable or disable sync
     */
    toggleSync(instanceId: string, enabled: boolean): void {
        const instance = this.instances.get(instanceId);
        if (instance) {
            this.instances.set(instanceId, {
                ...instance,
                metadata: { ...instance.metadata, isSyncEnabled: enabled }
            });
            this.notifyListeners();
        }
    }

    /**
     * Update instance position (only thing that can be changed locally)
     * @param instanceId Instance ID
     * @param position New position
     */
    updateInstancePosition(
        instanceId: string,
        position: { x: number; y: number }
    ): void {
        const instance = this.instances.get(instanceId);
        if (instance) {
            const deltaX = position.x - instance.position.x;
            const deltaY = position.y - instance.position.y;

            this.instances.set(instanceId, {
                ...instance,
                position: { ...position },
                items: instance.items.map(item => item.position ? ({
                    ...item,
                    position: {
                        x: item.position.x + deltaX,
                        y: item.position.y + deltaY
                    }
                }) : item)
            });

            this.notifyListeners();
        }
    }

    /**
     * Get instance by ID
     * @param instanceId Instance ID
     * @returns GroupInstance | undefined
     */
    getInstance(instanceId: string): GroupInstance | undefined {
        return this.instances.get(instanceId);
    }

    /**
     * Get all instances for a master group
     * @param masterGroupId Master group UUID
     * @returns GroupInstance[]
     */
    getInstancesByMasterGroup(masterGroupId: string): GroupInstance[] {
        return Array.from(this.instances.values()).filter(
            instance => instance.masterGroupId === masterGroupId
        );
    }

    /**
     * Get all instances that are out of sync
     * @returns Promise<GroupInstance[]>
     */
    async getOutOfSyncInstances(): Promise<GroupInstance[]> {
        const outOfSync: GroupInstance[] = [];

        for (const instance of this.instances.values()) {
            if (await this.needsSync(instance.id)) {
                outOfSync.push(instance);
            }
        }

        return outOfSync;
    }

    /**
     * Get all instances
     * @returns GroupInstance[]
     */
    getAllInstances(): GroupInstance[] {
        return Array.from(this.instances.values());
    }

    /**
     * Load instances from saved diagram data
     * @param instances Array of GroupInstance objects
     */
    loadInstances(instances: GroupInstance[]): void {
        this.clear();

        instances.forEach(instance => {
            // The manager owns a mutable lifetime separate from imported data.
            // Group instances are persisted as JSON, so a JSON clone is both
            // sufficient and compatible with the app's test/runtime targets.
            const ownedInstance = JSON.parse(JSON.stringify(instance)) as GroupInstance;
            this.instances.set(instance.id, ownedInstance);
        });

        this.notifyListeners();
    }

    /**
     * Delete an instance
     * @param instanceId Instance ID to delete
     */
    deleteInstance(instanceId: string): void {
        this.instances.delete(instanceId);
        this.notifyListeners();
    }

    /**
     * Subscribe to instance changes
     * @param listener Callback function
     * @returns Unsubscribe function
     */
    subscribe(listener: () => void): () => void {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    /**
     * Clear all instances
     */
    clear(): void {
        this.instances.clear();
        this.notifyListeners();
    }

    // Private helper methods

    /**
     * Transform master items to instance items with new IDs
     * Positions are offset by instance position
     */
    private transformMasterItems(
        masterItems: any[],
        instancePosition: { x: number; y: number }
    ): any[] {
        return masterItems.map(masterItem => {
            const newItem = {
                ...masterItem,
                id: uuidv4(), // Generate new instance-specific ID
            };

            // Handle tile-based positioning (used by ITEM, RECTANGLE, TEXTBOX)
            if (masterItem.tile) {
                newItem.tile = {
                    x: masterItem.tile.x + instancePosition.x,
                    y: masterItem.tile.y + instancePosition.y
                };
            }

            // Handle position-based positioning (alternative format)
            if (masterItem.position) {
                newItem.position = {
                    x: masterItem.position.x + instancePosition.x,
                    y: masterItem.position.y + instancePosition.y
                };
            }

            return newItem;
        });
    }

    /**
     * Create mapping from master item IDs to instance item IDs
     * Preserves existing instance IDs during sync
     */
    private createIdMapping(
        instanceItems: any[],
        masterItems: any[]
    ): Map<string, string> {
        const mapping = new Map<string, string>();

        // Map by index (assumes order is preserved)
        masterItems.forEach((masterItem, index) => {
            if (instanceItems[index]) {
                mapping.set(masterItem.id, instanceItems[index].id);
            }
        });

        return mapping;
    }

    /**
     * Transform master items using ID mapping
     * Preserves instance IDs where possible, generates new ones for new items
     */
    private transformMasterItemsWithMapping(
        masterItems: any[],
        instancePosition: { x: number; y: number },
        idMapping: Map<string, string>
    ): any[] {
        return masterItems.map(masterItem => {
            const instanceId = idMapping.get(masterItem.id) || uuidv4();

            const newItem = {
                ...masterItem,
                id: instanceId,
            };

            // Handle tile-based positioning (used by ITEM, RECTANGLE, TEXTBOX)
            if (masterItem.tile) {
                newItem.tile = {
                    x: masterItem.tile.x + instancePosition.x,
                    y: masterItem.tile.y + instancePosition.y
                };
            }

            // Handle position-based positioning (alternative format)
            if (masterItem.position) {
                newItem.position = {
                    x: masterItem.position.x + instancePosition.x,
                    y: masterItem.position.y + instancePosition.y
                };
            }

            return newItem;
        });
    }

    /**
     * Notify all listeners of changes
     */
    private notifyListeners(): void {
        this.listeners.forEach(listener => listener());
    }
}

// Singleton instance
export const groupInstanceManager = new GroupInstanceManager();
